import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { FeedbackSupplierProfile } from '@erp/rpc';

import { orpc, rpcClient } from '@/api/rpc/client';
import {
  isStagedFileMissing,
  startHubbellFeedbackRun,
  type HubbellFeedbackDrop,
  type HubbellFeedbackStartPhase,
} from '@/api/pricing/startHubbellFeedbackRun';
import {
  classifyWorkbook,
  dropState,
  type DroppedHubbellFile,
} from './classifyFiles';
import { peekWorkbook } from './peekWorkbook';
import { openDownload } from './openDownload';
import { LATEST_RUN_POLLING, startBlocker } from './runDisplay';
import { useLatestRun } from './useLatestRun';

/**
 * A supplier's Feedback card state: the files dropped and what each is,
 * the organization's latest run for that supplier (polled while it goes, so
 * leaving the page and coming back finds it), and the upload-and-start in
 * flight.
 */
export function useHubbellFeedback(supplier: FeedbackSupplierProfile) {
  const { allowed, runQuery, run, setLatestRun } = useLatestRun(
    orpc.pricing.hubbellFeedback.getLatestRun.queryOptions({
      input: { supplier: supplier.supplier },
      ...LATEST_RUN_POLLING,
    }),
  );

  const [entries, setEntries] = useState<DroppedHubbellFile[]>([]);
  const [noSalesFileTabs, setNoSalesFileTabs] = useState<string[]>([]);
  /**
   * FINAL.xlsx stays on the card after a run and its upload is reused by the
   * next start (94 MB; the same file for every Division), until it is removed
   * or another Results Workbook replaces it.
   */
  const [staged, setStaged] = useState<{
    entryId: string;
    uploadKey: string;
  } | null>(null);
  const nextId = useRef(0);
  /** Files removed while their classification was still pending: its late result is ignored. */
  const removed = useRef(new Set<string>());
  const [phase, setPhase] = useState<HubbellFeedbackStartPhase | null>(null);
  const [downloadingTab, setDownloadingTab] = useState<string | null>(null);

  async function classify(entry: DroppedHubbellFile): Promise<void> {
    let kind: DroppedHubbellFile['kind'];
    try {
      kind = classifyWorkbook(await peekWorkbook(entry.file), supplier);
    } catch {
      kind = 'unreadable';
    }
    if (removed.current.has(entry.id)) return;
    // Ids grow in drop order: the Results Workbook dropped last wins, whatever
    // order the reads finish in.
    const newer = (item: DroppedHubbellFile) =>
      Number(item.id) > Number(entry.id);
    setEntries((current) => {
      if (!current.some((item) => item.id === entry.id)) return current;
      const replaced =
        kind === 'results' &&
        current.some((item) => item.kind === 'results' && newer(item));
      return current.flatMap((item) => {
        if (item.id === entry.id) return replaced ? [] : [{ ...item, kind }];
        // A new Results Workbook replaces the one on the card.
        return kind === 'results' && item.kind === 'results' && !newer(item)
          ? []
          : [item];
      });
    });
    if (kind === 'results') setStaged(null);
  }

  /** Adds the files as "reading" and classifies each from its first rows. */
  function addFiles(files: File[]): void {
    const added = files.map((file): DroppedHubbellFile => {
      nextId.current += 1;
      return { id: String(nextId.current), file, kind: 'reading' };
    });
    setEntries((current) => [...current, ...added]);
    for (const entry of added) void classify(entry);
  }

  const drop = dropState(entries, noSalesFileTabs);

  function removeFile(id: string): void {
    removed.current.add(id);
    setEntries((current) => current.filter((entry) => entry.id !== id));
    setStaged((current) => (current?.entryId === id ? null : current));
    // A Division whose accepted file is removed drops its "no sales file"
    // mark too, so the file's removal never silently turns into Sales 0.
    for (const [tab, placed] of drop.salesFileByTab)
      if (placed.id === id)
        setNoSalesFileTabs((current) => current.filter((t) => t !== tab));
  }

  /** Marks a Division to build with Sales 0, or unmarks it. */
  function toggleNoSalesFile(tab: string): void {
    setNoSalesFileTabs((current) =>
      current.includes(tab)
        ? current.filter((item) => item !== tab)
        : [...current, tab],
    );
  }

  const start = useMutation({
    mutationFn: async (input: HubbellFeedbackDrop) => {
      try {
        return await startHubbellFeedbackRun(input, setPhase);
      } catch (error) {
        // The reused FINAL.xlsx is gone from staging (another start deleted
        // it): upload it once more rather than failing the user.
        if (input.resultsUploadKey !== undefined && isStagedFileMissing(error))
          return startHubbellFeedbackRun(
            { ...input, resultsUploadKey: undefined },
            setPhase,
          );
        throw error;
      }
    },
    onSuccess: (started) => {
      // The started (or already active) run is the latest; polling takes over.
      setLatestRun(started.run);
      // FINAL.xlsx stays, staged, for the next run; the rest of the drop is done.
      const results = entries.find((entry) => entry.kind === 'results');
      setEntries(results ? [results] : []);
      setStaged(
        results
          ? { entryId: results.id, uploadKey: started.resultsUploadKey }
          : null,
      );
      setNoSalesFileTabs([]);
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => setPhase(null),
  });

  async function download(tab: string): Promise<void> {
    if (!run) return;
    setDownloadingTab(tab);
    try {
      const file =
        await rpcClient.pricing.hubbellFeedback.getDivisionDownloadUrl({
          backgroundJobId: run.backgroundJobId,
          tab,
        });
      openDownload(file.url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    }
    setDownloadingTab(null);
  }

  return {
    run,
    runLoading: runQuery.isLoading,
    runError: runQuery.isError,
    drop,
    addFiles,
    removeFile,
    toggleNoSalesFile,
    phase,
    blocker: startBlocker({
      drop,
      run,
      allowed,
      starting: start.isPending,
    }),
    /** Uploads the drop and starts the run; nothing happens before the Results Workbook is in. */
    start: () => {
      if (!drop.resultsWorkbook) return;
      const results = entries.find((entry) => entry.kind === 'results');
      start.mutate({
        supplier: supplier.supplier,
        resultsWorkbook: drop.resultsWorkbook,
        resultsUploadKey:
          staged && results && staged.entryId === results.id
            ? staged.uploadKey
            : undefined,
        salesFiles: drop.salesFiles,
        noSalesFileTabs: [...drop.noSalesFileTabs],
      });
    },
    /** The id of the dropped FINAL.xlsx whose upload the next start reuses. */
    stagedResultsId: staged?.entryId ?? null,
    download,
    downloadingTab,
  };
}
